import { currencyFormatter } from 'tio-ui/format';
import type { Loan } from './loan.type.ts';
export interface LoanExtension extends Loan {}
export class LoanExtension {
  get hasPendingResolutionReview() {
    // == null catches both an explicit null from the API and an omitted
    // attr (which Ember Data surfaces as undefined).
    return this.resolutionSource === 'opensearch' && this.adminAction == null;
  }

  get isResolutionAdminConfirmed() {
    return this.adminAction === 'confirmed';
  }

  get isResolutionAdminRejected() {
    return this.adminAction === 'rejected';
  }

  /**************************
   **  Computed Properties **
   **************************/
  get currentBalance() {
    return this.observation?.balance || 0;
  }

  get currentBalanceDollars() {
    return this.observation?.balance / 100 || 0;
  }

  get interestRate() {
    return this.observation?.['interest-rate'];
  }

  get interestBalance() {
    return this.observation?.['interest-balance'] || 0;
  }

  get principalBalance() {
    return this.observation?.['principal-balance'] || 0;
  }

  get originalAmount() {
    return this.observation?.['original-amount'];
  }

  get originalDate() {
    return this.observation?.['original-date'];
  }

  get nextPaymentAmount() {
    return this.observation?.['next-payment-amount'];
  }

  get nextPaymentDate() {
    return this.observation?.['next-payment-date'];
  }

  get lastUpdatedOn() {
    return this.observation?.at;
  }

  get hasNsldsPslfData() {
    const qualifyingPayments = this.observation?.['forgiveness-program-qualified'];
    return qualifyingPayments !== undefined && qualifyingPayments !== null;
  }

  get nsldsUpdatedAt() {
    return this.observation?.at ?? this.observation?.['balance-date'] ?? null;
  }

  get repaymentPlan() {
    return this.observation?.['repayment-plan-derived'];
  }

  get status() {
    return this.observation?.['status-derived'];
  }

  get isPaidOff() {
    return this.observation?.['is-paid-off'] || false;
  }

  get loanClass() {
    return this.observation?.['loan-class'];
  }

  get name() {
    return this.observation?.['loan-name'];
  }

  get currentBalanceFormatted() {
    const dollars = this.currentBalance;
    return currencyFormatter(undefined, 0).format(dollars);
  }

  // originalDisbursementAmount is stored in cents; format as USD for display.
  get originalDisbursementAmountFormatted() {
    if (this.originalDisbursementAmount == null) return undefined;

    return currencyFormatter(undefined).format(this.originalDisbursementAmount / 100);
  }

  get loanTypeDisplayName() {
    return this.observation?.['raw-loan-type'];
  }

  get isValidForProjections() {
    const { observation, interestRate, principalBalance, nextPaymentAmount, nextPaymentDate } =
      this;
    if (!observation) {
      return false;
    }

    return principalBalance && interestRate && nextPaymentAmount && nextPaymentDate;
  }
}

const Registration = {
  kind: 'object',
  name: 'loan',
  features: LoanExtension,
};
export default Registration;
